import {
  Image,
  Center,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  Text,
  PopoverContent,
  PopoverTrigger,
  useMediaQuery,
  List,
  ListItem,
  useColorModeValue,
  Box,
  Link,
  IconButtonProps,
  Heading,
  VStack
} from '@chakra-ui/react'

import { Property } from 'generated/graphql'

import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { VscBell } from 'react-icons/vsc'

import { amountFormatter, formatPropertyTitle } from 'utils'
import { useHistory } from 'react-router'
import { useAuthContext } from 'context/AuthProvider'
import { FavoriteProperty } from 'components/Property'
import Markdown from 'components/Markdown'

import { SignSuggestion } from 'components'
import CustomTooltip from 'components/CustomTooltip'
import { useGeolocation } from 'hooks'

type NotificationProps = {
  title?: string
  label?: string
  icon?: ReactElement
  properties?: Property[]
} & IconButtonProps

const Notification: React.FC<NotificationProps> = ({ title, icon, properties, label, ...rest }) => {
  const [isTabletOrMobile] = useMediaQuery('(max-width: 40em)')
  const { t, i18n } = useTranslation()
  const translator = i18n as any
  const lang = translator.language as string
  const history = useHistory()
  const bg = useColorModeValue('gray.50', 'gray.700')
  const { currency } = useGeolocation()
  const { isAuthenticated } = useAuthContext()
  return (
    <Popover placement="auto">
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <Center flexDirection="column">
              <Center position="relative" alignItems="center">
                <CustomTooltip label={title as string}>
                  <IconButton variant="solid" isRound colorScheme="gray" icon={icon} {...rest} />
                </CustomTooltip>

                {properties && properties?.length > 0 && (
                  <Center
                    position="absolute"
                    fontSize="xx-small"
                    top={0}
                    color="white"
                    borderRadius="full"
                    boxShadow="0 0 .2rem red"
                    bg="red.300"
                    fontWeight="semibold"
                    pointerEvents="none"
                    right={0}
                    transform="translateY(-50%)"
                    width={4}
                    height={4}
                  >
                    {properties?.length}
                  </Center>
                )}
              </Center>
              {label && <Text fontSize="xs">{label}</Text>}
            </Center>
          </PopoverTrigger>
          <PopoverContent width={isTabletOrMobile ? '100vw' : 430}>
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverBody
              overflowY="auto"
              zIndex={48}
              height={isTabletOrMobile ? '83vh' : '50vh'}
              overflowX="hidden"
              px={0}
              py={2}
              bg={bg}
            >
              {properties && properties?.length > 0 ? (
                <List>
                  {properties?.map((property) => (
                    <ListItem cursor="pointer" position="relative" key={property?.id}>
                      <Box
                        p={4}
                        onClick={() => {
                          history.push(
                            isAuthenticated
                              ? `/auth/property/${property.id}`
                              : `/property/${property.id}`
                          )
                          onClose()
                        }}
                        display={{ md: 'flex' }}
                      >
                        <Box flexShrink={0}>
                          {property?.photos?.[0]?.url && (
                            <Image
                              borderRadius="lg"
                              width={{ md: 28 }}
                              src={`${property?.photos?.[0]?.url}`}
                              fallbackSrc={property?.photos?.[0]?.formats?.thumbnail?.url}
                              alt={property?.photos?.[0]?.alternativeText as string}
                            />
                          )}
                        </Box>
                        <Box mt={{ base: 4, md: 0 }} ml={{ md: 6 }}>
                          <Text
                            fontWeight="bold"
                            textTransform="uppercase"
                            fontSize="md"
                            letterSpacing="wide"
                            color="blue.500"
                          >
                            {amountFormatter(property?.price as number, currency)}
                          </Text>
                          <Link
                            mt={1}
                            display="block"
                            fontSize="sm"
                            lineHeight="normal"
                            fontWeight="semibold"
                            onClick={() =>
                              history.push(
                                isAuthenticated
                                  ? `/auth/property/${property.id}`
                                  : `/property/${property.id}`
                              )
                            }
                          >
                            {formatPropertyTitle(property as Property, t, lang)}
                          </Link>
                          <Box mt={2} color="gray.500" fontSize="xs" noOfLines={3}>
                            <Markdown content={property?.description} />
                          </Box>
                        </Box>
                      </Box>
                      <FavoriteProperty
                        position="absolute"
                        top={0}
                        right={0}
                        padding={[3, 1]}
                        remove
                        my={2}
                        mx={3}
                        propertyData={property as Property}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box minH="48vh" py={4} flexDirection="column">
                  <VStack alignItems="flex-start" spacing={10} px={6}>
                    <Box>
                      <Heading as="h4" fontSize="3xl" fontWeight="extrabold" my={4}>
                        Save your favourites
                      </Heading>
                      <Text fontSize="xs">
                        When search for properties, save your favourites by selecting the heart
                      </Text>
                    </Box>

                    <Text fontSize="xs">
                      That way, you can view them later at a click of a button.{' '}
                      <b>Start a new search to begin.</b>
                    </Text>
                  </VStack>
                  {!isAuthenticated && <SignSuggestion />}
                </Box>
              )}
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  )
}
Notification.defaultProps = {
  title: 'Notifications',
  icon: <VscBell size={14} />
}
export default Notification
